import { v4 as uuidv4 } from 'uuid';
import { selectCaptionsSpokenLanguage } from 'features/transcripts/slices/captions/captionsSlice';
import {
  ExternalStreamingStarted,
  ExternalStreamingStopped,
  StreamingStarted,
  StreamingStopped,
} from 'features/streaming/types';
import { SignalingUserLeftPayload, UserId } from 'features/users/types';
import { store } from 'store/store';
import { eventMap } from 'services/signaling/eventMap';
import { roomOnlyEvents, RoomOnlyEvents } from 'services/signaling/roomOnlyEvents';
import {
  CloseCodes,
  SignalingReceivedMessage,
  SignalingRoomUser,
  SignalingSendMessage,
} from 'services/signaling/types';
import { logger, logSignalingEvent } from 'utils/logger';
import { redactIceServers } from 'utils/sentry/redactIceServers';
import {
  selectDeviceInfo,
  selectDeviceType,
  selectSignalingInitData,
  signalingReconnectionStarted,
  STORM_THRESHOLD,
} from 'features/application/applicationSlice';
import { roomError, selectRoomId } from 'features/room/roomSlice';
import i18n from 'i18n';
import { retryDelay } from 'services/signaling/retryDelay';
import { isError } from 'utils/types';
import { getBrowserId } from 'utils/getBrowserId';
import * as Sentry from '@sentry/react';
import { signalingReconnected } from 'features/application/actions';
import { selectIsRecorderSession, selectRecorderStatus } from 'features/recorder/recorderSlice';
import { stopRecorder } from 'features/recorder/actions';
import { prepareRoomJoin, signalingRoomJoined } from 'features/room/actions';
import { E2EEManager, e2eeNameSensitiveEvents } from 'features/e2ee/E2EEManager';
import { RoomJoinedPayload } from 'features/room/types';
import { PhoneUsersJoinedPayload, PhoneUsersLeftPayload, TelephonyUser } from 'features/sip/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import { selectMobileScreenshare } from 'features/sdk/sdkStateSlice';
import { eventBus } from 'utils/eventBus';

type PendingMessage = {
  joinAction?: ['userJoined', SignalingRoomUser] | ['userLeft', SignalingUserLeftPayload];
  streamActions: (['streamStarted', StreamingStarted] | ['streamStopped', StreamingStopped])[];
  screenshareActions: (
    ['screenshareStarted', StreamingStarted] | ['screenshareStopped', StreamingStopped]
  )[];
};

class SignalingSocket {
  ws: WebSocket | null = null;

  retries: number = 3;

  retryAttempt: number = 0;

  retryTimeout?: number;

  pingInterval?: number;

  connectionFailure: boolean = false;

  // Sampled when a deliberate close flushes the tail. A readyState other than OPEN
  // means send() refused the batch, so zero queued bytes there is a no-op rather
  // than a clean handover. Absent entirely means close() never ran.
  private closeFlushDiagnostics?: { bufferedAmountAfterFlush: number; readyStateAtFlush: number };

  isConnectionEstablished: boolean = false;

  isRoomJoined: boolean = false;

  isReconnecting: boolean = false;

  pendingUpdates: Record<UserId, PendingMessage> = {};

  pendingSipUpdates: (
    ['phoneUsersJoined', PhoneUsersJoinedPayload] | ['phoneUsersLeft', PhoneUsersLeftPayload]
  )[] = [];

  pendingExternalStreamsUpdates: (
    | ['externalStreamStarted', ExternalStreamingStarted]
    | ['externalStreamStopped', ExternalStreamingStopped]
  )[] = [];

  totalInterceptedUpdates = 0;

  roomJoinData?: RoomJoinedPayload;

  pendingRequests: Record<
    string,
    {
      resolve: (value: any) => void;
      reject: (value: any) => void;
    }
  > = {};

  connect = (url: string, token: string, uniqueId: string) => {
    if (!url) {
      Sentry.captureException(new Error('Signaling socket connect called without url'));
      return;
    }

    // A fresh socket invalidates the previous close's measurement.
    this.closeFlushDiagnostics = undefined;

    const paramsStr = this.getConnectionParams(token, uniqueId);
    try {
      this.ws = new WebSocket(`${url}?${paramsStr}`);
    } catch (error) {
      logger.remote().error('Signaling socket: failed to create WebSocket', error);
      Sentry.captureException(error, {
        contexts: {
          socket: {
            url,
            token,
            uniqueId,
          },
        },
      });
      this.connectionFailure = true;
      store.dispatch(eventMap.disconnect({ error: true }));
      return;
    }

    this.ws.onopen = this.onOpen;
    this.ws.onmessage = this.onMessage;
    this.ws.onclose = this.onClose;

    this.ws.onerror = (event) => {
      logger.remote().error('Signaling socket error', event);

      this.isReconnecting = false;
      if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
        this.ws.close();
      }
    };
  };

  close = (code?: number, reason?: string) => {
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      // Last chance to deliver the tail: a deliberate close is often terminal
      // (recorder death, session end) and nothing drains it afterwards.
      logger.flush();
      this.closeFlushDiagnostics = {
        bufferedAmountAfterFlush: this.ws.bufferedAmount,
        readyStateAtFlush: this.ws.readyState,
      };

      this.ws.close(code || 1000, reason);
    }
  };

  send = (message: SignalingSendMessage): boolean => {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    }

    // The remote logger flushes through send(); warning on its own dropped
    // batch would re-feed the queue it is draining.
    if (message.event !== 'log') {
      logger.remote().warn('Signaling send dropped: socket not open', {
        event: message.event,
        readyState: this.ws?.readyState,
      });
    }

    return false;
  };

  sendAsync = <T = unknown>(message: SignalingSendMessage) => {
    const requestId = uuidv4();

    message.requestId = requestId;

    return new Promise<T>((resolve, reject) => {
      this.pendingRequests[requestId] = { resolve, reject };
      this.send(message);
    });
  };

  resetState = () => {
    this.ws = null;
    this.retries = 3;
    this.retryAttempt = 0;
    this.retryTimeout = undefined;
    this.pingInterval = undefined;
    this.connectionFailure = false;
    this.closeFlushDiagnostics = undefined;
    this.isConnectionEstablished = false;
    this.isReconnecting = false;
    this.isRoomJoined = false;
    this.pendingUpdates = {};
    this.pendingSipUpdates = [];
    this.pendingExternalStreamsUpdates = [];
    this.totalInterceptedUpdates = 0;
    this.roomJoinData = undefined;
    this.pendingRequests = {};
  };

  saveMessage = (kind: RoomOnlyEvents, data: any) => {
    if (data.id) {
      logger
        .remote({ system: true, tier: 1 })
        .debug(
          'SignalingSocket, intercepted user update prior to joining room, userId: ',
          data.id,
          'action: ',
          kind
        );
    } else {
      logger
        .remote({ system: true, tier: 1 })
        .debug('SignalingSocket, intercepted an event prior to joining room, action:', kind);
    }

    this.totalInterceptedUpdates += 1; // slightly off, we don't care

    if (data.id) {
      this.pendingUpdates[data.id] ??= {
        screenshareActions: [],
        streamActions: [],
      };
    }

    if (kind === 'userJoined' || kind === 'userLeft') {
      // join events are mutually exclusive, so we just keep
      // the last one and will handle it upon roomJoined
      this.pendingUpdates[data.id].joinAction = [kind, data];
    }

    // as for stream/screenshare events we store them historically
    // and reconcile the valid final state upon roomJoined
    if (kind === 'streamStarted' || kind === 'streamStopped') {
      this.pendingUpdates[data.id].streamActions.push([kind, data]);
    }

    if (kind === 'screenshareStarted' || kind === 'screenshareStopped') {
      this.pendingUpdates[data.id].screenshareActions.push([kind, data]);
    }

    if (kind === 'externalStreamStarted' || kind === 'externalStreamStopped') {
      this.pendingExternalStreamsUpdates.push([kind, data]);
    }

    // handle phone join events similar to streams as they contain an array of users
    // and we can't store them by id. They will be reconciled upon roomJoined
    if (kind === 'phoneUsersJoined' || kind === 'phoneUsersLeft') {
      this.pendingSipUpdates.push([kind, data]);
    }
  };

  preparePendingMessages = (roomJoinedPayload: RoomJoinedPayload) => {
    const updates = Object.entries(this.pendingUpdates);

    logger
      .remote({ system: true, tier: 1 })
      .debug(`SignalingSocket, processing ${this.totalInterceptedUpdates} intercepted messages`);

    const { users } = roomJoinedPayload;

    const userList: Record<UserId, SignalingRoomUser> = {};
    for (const user of users) {
      userList[user.id] = user;
    }

    updates.forEach(([userId, userUpdate]) => {
      // handle users's joined state first
      if (userUpdate.joinAction) {
        const [joinActionKind, joinActionPayload] = userUpdate.joinAction;

        if (joinActionKind === 'userLeft') {
          // recentest update from signaling was that user has left.
          // we don't care for streams in this case, just drop the user from
          // roomJoined users list like they were never there

          if (userList[userId]) {
            delete userList[userId];
          }

          return;
        }

        if (!userList[userId]) {
          // if update was that user joined and user is not included in roomJoined, add them.
          userList[userId] = joinActionPayload;
        }
      }

      if (!userList[userId] && !userUpdate.joinAction) {
        // no join action neither roomJoined data. streams seem to be invalid
        logger
          .remote({ system: true })
          .warn(
            'SignalingSocket, broken state when reconciling: have updates for user that didn’t send neither roomJoined nor userJoined, userId',
            userId,
            'stream actions: ',
            userUpdate.streamActions.length,
            'screenshare actions: ',
            userUpdate.screenshareActions.length
          );
        return;
      }

      // if we've made it to here, everything checks out and we can rely on having user in roomJoined collection.
      // so we've got to update their stream state
      userList[userId].streams ??= [];

      for (const [actionKind, actionData] of userUpdate.streamActions) {
        if (actionKind === 'streamStarted') {
          // Apply feedId from intercepted streamStarted if the user doesn't have one yet
          // (race condition: roomJoined may arrive before feedId is assigned)
          if (actionData.feedId && !userList[userId].feedId) {
            userList[userId].feedId = actionData.feedId;
          }

          for (const stream of actionData.streams) {
            if (
              !userList[userId].streams.some(
                (lookupStream) =>
                  lookupStream.mid === stream.mid && lookupStream.type === stream.type
              )
            ) {
              userList[userId].streams.push(stream);
            }
          }
        } else {
          // we've intercepted streamStopped, kill (if found) that mid in roomJoined
          userList[userId].streams = userList[userId].streams.filter(
            (stream) => stream.mid !== actionData.mid
          );
        }
      }

      // if no screenshare changes intercepted, do nothing;
      if (!userUpdate.screenshareActions.length) {
        return;
      }

      let screenshareStreams = userList[userId].screenshareStreams || [];

      for (const [actionKind, actionData] of userUpdate.screenshareActions) {
        if (actionKind === 'screenshareStarted') {
          // update user's screenshare feed id with the most recent one.
          // theoretically this will never change but still feels right;
          userList[userId].screenshareFeedId = actionData.feedId;
          // check if that stream is included in roomJoined payload
          // if not, add it
          for (const stream of actionData.streams) {
            if (
              !screenshareStreams.some(
                (lookupStream) =>
                  lookupStream.mid === stream.mid && lookupStream.type === stream.type
              )
            ) {
              screenshareStreams.push(stream);
            }
          }
        } else {
          // delete stopped stream
          screenshareStreams = screenshareStreams.filter((stream) => stream.mid !== actionData.mid);
        }
      }

      // update associated screensharing vars;

      userList[userId].screensharing = !!screenshareStreams.length;
      userList[userId].startScreenshareTimestamp = Number(new Date()); // =/
      userList[userId].screenshareStreams = screenshareStreams;
    });

    let externalStreams: ExternalStreamingStarted[] = [];

    for (const [actionKind, actionData] of this.pendingExternalStreamsUpdates) {
      if (actionKind === 'externalStreamStarted') {
        externalStreams.push(actionData);
      } else if (actionKind === 'externalStreamStopped') {
        externalStreams = externalStreams.filter((stream) => stream.feedId !== actionData.feedId);
      }
    }

    // process sip users
    let sipUsers: TelephonyUser[] = roomJoinedPayload.telephony
      ? [...roomJoinedPayload.telephony.users]
      : [];

    // create a Set of existing user IDs for quick lookup
    const existingUserIds = new Set(sipUsers.map((user) => user.id));

    for (const [actionKind, actionData] of this.pendingSipUpdates) {
      if (actionKind === 'phoneUsersJoined') {
        // add user only if not already present in the initial roomJoined payload
        for (const user of actionData.users) {
          if (!existingUserIds.has(user.id)) {
            sipUsers.push(user);
            existingUserIds.add(user.id);
          }
        }
      } else if (actionKind === 'phoneUsersLeft') {
        // remove left users
        const idsToRemove = new Set(actionData.users);
        sipUsers = sipUsers.filter((user) => !idsToRemove.has(user.id));

        for (const userId of idsToRemove) {
          existingUserIds.delete(userId);
        }
      }
    }

    this.pendingUpdates = {};
    this.pendingSipUpdates = [];
    this.pendingExternalStreamsUpdates = [];

    // A buffered userJoined may be newer than the snapshot, so the user is admitted.
    // Any remaining overlap resolves in favour of the waiting list.
    const admittedIds = new Set(
      updates
        .filter(([, userUpdate]) => userUpdate.joinAction?.[0] === 'userJoined')
        .map(([userId]) => userId)
    );
    const { usersAwaitingEntry = [] } = roomJoinedPayload;
    const reconciledUsersAwaitingEntry = usersAwaitingEntry.filter(
      (user) => !admittedIds.has(user.id)
    );
    const awaitingIds = new Set(reconciledUsersAwaitingEntry.map((user) => user.id));
    const newUserList = Object.values(userList)
      .filter(Boolean)
      .filter((user) => !awaitingIds.has(user.id));

    // override initial state with the corrected one;
    const updatedRoomJoinedPayload = {
      ...roomJoinedPayload,
      users: newUserList,
      usersAwaitingEntry: reconciledUsersAwaitingEntry,
      externalStreams,
    };

    if (roomJoinedPayload.telephony) {
      updatedRoomJoinedPayload.telephony = {
        ...roomJoinedPayload.telephony,
        users: sipUsers,
      };
    }

    return updatedRoomJoinedPayload;
  };

  joinRoom = () => {
    this.isRoomJoined = true;

    const updatedJoinPayload = this.preparePendingMessages(this.roomJoinData!);
    store.dispatch(signalingRoomJoined(updatedJoinPayload));

    this.roomJoinData = undefined;
  };

  private reconnect() {
    const initData = selectSignalingInitData(store.getState());
    const browserId = getBrowserId();

    this.connect(initData.url, initData.token, browserId);
  }

  private ping() {
    const timeout = 25 * 1000;

    if (this.pingInterval) {
      window.clearInterval(this.pingInterval);
    }

    this.pingInterval = window.setInterval(() => {
      this.send({ event: 'ping', data: {} });
    }, timeout);
  }

  private shouldRetry = (e: CloseEvent): boolean => {
    const isRecorder = selectIsRecorderSession(store.getState());

    if (isRecorder) {
      // Recorder sessions must never reconnect: the backend starts a brand-new
      // recorder on death, so a client retry would produce a duplicate recording.
      const recorderStatus = selectRecorderStatus(store.getState());
      const closeContext = {
        closeCode: e.code,
        closeReason: e.reason,
        wasClean: e.wasClean,
        recorderStatus,
        ...this.closeFlushDiagnostics,
      };

      // TERMINATED is the code this client sends itself when the session ends, so it is not a death.
      if (e.code === CloseCodes.TERMINATED) {
        logger
          .remote({ system: true, capture: 'signaling' })
          .info('Recorder signaling socket closed on session end', closeContext);
      } else {
        logger
          .remote({ system: true, capture: 'signaling' })
          .error('Recorder signaling socket closed, terminating recorder session', closeContext);
        Sentry.captureException(
          new Error(`Recorder signaling socket closed (code=${e.code}, status=${recorderStatus})`),
          {
            tags: { feature: 'recorder', closeCode: String(e.code), recorderStatus },
            extra: closeContext,
          }
        );
      }

      store.dispatch(stopRecorder());
      return false;
    }

    if (e.code === CloseCodes.SYSTEM_KICK) {
      store.dispatch(
        roomError({
          global: true,
          name: 'socket-kick',
          title: i18n.t('waiting-screens:room.room_kick.title'),
          message: i18n.t('waiting-screens:room.room_kick.text'),
        })
      );

      return false;
    }

    if (e.code === CloseCodes.NO_RECONNECT || e.code === CloseCodes.TERMINATED) {
      return false;
    }

    if (
      e.code === CloseCodes.INTERNAL_ERROR ||
      e.reason === 'invalid_unique_id' ||
      e.reason === 'invalid_token'
    ) {
      this.connectionFailure = true;
      return false;
    }

    if (this.retryAttempt <= this.retries) {
      return true;
    }

    this.connectionFailure = true;

    return false;
  };

  private onMessage = (e: MessageEvent) => {
    try {
      const message: SignalingReceivedMessage = JSON.parse(e.data);
      const messageStr = `Received signaling message, event: ${message.event}`;

      logSignalingEvent(messageStr, message);

      Sentry.addBreadcrumb({
        category: 'streaming',
        message: messageStr,
        data: { ...message, data: redactIceServers(message.data) },
      });

      const action = eventMap[message.event];

      if (message.requestId) {
        const resolvers = this.pendingRequests[message.requestId];

        if (resolvers) {
          if (message.success) {
            resolvers.resolve(message.data);
          } else {
            resolvers.reject(message.data);
          }
        }

        if (action) {
          store.dispatch(action(message.data));
        }

        return;
      }

      if (!action) {
        return;
      }

      if (!this.isRoomJoined && roomOnlyEvents.includes(message.event)) {
        this.saveMessage(message.event as RoomOnlyEvents, message.data);

        return;
      }

      if (message.event === 'roomJoined') {
        this.roomJoinData = message.data;

        store.dispatch(
          prepareRoomJoin({
            roomId: message.data.roomId,
            roomPin: message.data.roomPin,
            mountpointId: message.data.mountpointId,
            rtcStatsServer: message.data.rtcStatsServer,
          })
        );

        return;
      }

      // @TODO remove this once we have real quiz templates
      // Intercept quizTemplatesReceived and inject mock templates if empty
      // if (message.event === 'quizTemplatesReceived') {
      //   const mockQuizTemplates = [
      //     {
      //       id: 'template-1',
      //       title: 'JavaScript Fundamentals',
      //       secondsRemaining: 300,
      //       showCorrectAnswers: true,
      //       questions: [
      //         {
      //           id: 'q1',
      //           text: 'What is the result of typeof null in JavaScript?',
      //           multipleChoices: false,
      //           choices: [
      //             { text: 'null', correct: false },
      //             { text: 'object', correct: true },
      //             { text: 'undefined', correct: false },
      //             { text: 'number', correct: false },
      //           ],
      //         },
      //         {
      //           id: 'q2',
      //           text: 'Which of the following are truthy values in JavaScript?',
      //           multipleChoices: true,
      //           choices: [
      //             { text: '0', correct: false },
      //             { text: '"hello"', correct: true },
      //             { text: '[]', correct: true },
      //             { text: 'false', correct: false },
      //           ],
      //         },
      //         {
      //           id: 'q3',
      //           text: 'What does the spread operator (...) do?',
      //           multipleChoices: false,
      //           choices: [
      //             { text: 'Creates a new variable', correct: false },
      //             { text: 'Expands an iterable into individual elements', correct: true },
      //             { text: 'Multiplies numbers', correct: false },
      //             { text: 'Imports modules', correct: false },
      //           ],
      //         },
      //       ],
      //     },
      //     {
      //       id: 'template-2',
      //       title: 'React Hooks Quiz',
      //       secondsRemaining: 240,
      //       showCorrectAnswers: false,
      //       questions: [
      //         {
      //           id: 'q1',
      //           text: 'Which hook is used to manage state in functional components?',
      //           multipleChoices: false,
      //           choices: [
      //             { text: 'useEffect', correct: false },
      //             { text: 'useState', correct: true },
      //             { text: 'useContext', correct: false },
      //             { text: 'useReducer', correct: false },
      //           ],
      //         },
      //         {
      //           id: 'q2',
      //           text: 'Which hooks can be used for side effects?',
      //           multipleChoices: true,
      //           choices: [
      //             { text: 'useEffect', correct: true },
      //             { text: 'useLayoutEffect', correct: true },
      //             { text: 'useState', correct: false },
      //             { text: 'useMemo', correct: false },
      //           ],
      //         },
      //       ],
      //     },
      //   ];

      //   // Override the templates with mock data if the backend sends empty array
      //   const modifiedData = {
      //     ...message.data,
      //     templates: message.data.templates.length > 0 ? message.data.templates : mockQuizTemplates,
      //   };

      //   store.dispatch(action(modifiedData));
      //   return;
      // }

      if (E2EEManager.e2eeEnabled) {
        // @TODO figure out if it's still relevant approach since other events are handled in the corresponding sagas
        if (this.isRoomJoined && e2eeNameSensitiveEvents.has(message.event)) {
          const roomId = selectRoomId(store.getState());
          const localUserId = selectLocalUserId(store.getState());

          const eventInitiatorId: UserId = message.data.initiator?.id || message.data.id;

          if (localUserId !== eventInitiatorId) {
            const queued = E2EEManager.enqueuePendingAction(action(message.data), roomId);
            if (queued) {
              return;
            }
          }
        }
      }

      store.dispatch(action(message.data));
    } catch (error) {
      if (isError(error)) {
        Sentry.captureException(error);
        logger.remote().error('SignalingSocket', error.message);
      }
    }
  };

  private onOpen = () => {
    logger.remote().log('Signaling socket: Connection established');

    this.isConnectionEstablished = true;
    this.retryAttempt = 0;
    this.retries = 15;

    if (this.isReconnecting) {
      this.isReconnecting = false;
      store.dispatch(signalingReconnected());
    }

    this.ping();
  };

  private onClose = (e: CloseEvent) => {
    logger.remote().log('Signaling socket: Connection died', e);
    this.ws = null;

    if (this.retryTimeout) {
      window.clearTimeout(this.retryTimeout);
    }

    if (this.pingInterval) {
      window.clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }

    if (this.shouldRetry(e)) {
      if (this.isConnectionEstablished && !this.isReconnecting) {
        this.isReconnecting = true;

        // signalingReconnectionStarted can fire more than once per logical
        // reconnect cycle (ws.onerror → ws.close → onClose race). The reducer's
        // dedup gate in applicationSlice.ts keeps consecutiveCycles monotonic so
        // the `===` equality below fires the storm warning exactly once per
        // cycle. Do not remove either gate without re-evaluating storm detection.
        store.dispatch(signalingReconnectionStarted());

        const { consecutiveCycles } = store.getState().application.signaling;
        if (consecutiveCycles === STORM_THRESHOLD) {
          // navigator.connection (NetworkInformation API) is Chromium-only.
          // Edge/Chrome populate effectiveType/downlink/rtt; Firefox/Safari
          // leave the whole property undefined and the optional chain
          // serialises the fields as null in Sentry. That is the intended
          // behaviour — no try/catch needed.
          const { connection } = navigator as Navigator & {
            connection?: { effectiveType?: string; downlink?: number; rtt?: number };
          };

          const closeContext = {
            consecutiveCycles,
            closeCode: e.code,
            closeReason: e.reason,
            navigatorOnLine: navigator.onLine,
            effectiveType: connection?.effectiveType,
            downlink: connection?.downlink,
            rtt: connection?.rtt,
          };

          logger
            .remote({ system: true, capture: 'signaling' })
            .warn(`Signaling reconnect storm detected (cycles=${consecutiveCycles})`, closeContext);
        }
      }

      this.retryAttempt += 1;
      const delay = retryDelay(this.retryAttempt);

      logger.remote().log('Signaling socket: attempt to reconnect:', this.retryAttempt);
      logger.log('Signaling socket: delay', delay);

      this.retryTimeout = window.setTimeout(() => {
        this.reconnect();
      }, delay);
    } else {
      this.isConnectionEstablished = false;
      this.isReconnecting = false;

      store.dispatch(eventMap.disconnect({ error: this.connectionFailure }));

      if (this.connectionFailure) {
        eventBus.sendMessage('connectionFailure');
      }
    }
  };

  private getConnectionParams = (token: string, uniqueId: string) => {
    const browserDetails = selectDeviceInfo(store.getState());
    const deviceType = selectDeviceType(store.getState());
    const spokenLanguage = selectCaptionsSpokenLanguage(store.getState());
    const mobileApp = selectMobileScreenshare(store.getState());

    const params = new URLSearchParams({
      token,
      uniqueId,
      browserName: browserDetails.browser.name || '',
      browserVersion: browserDetails.browser.version || '',
      deviceType,
      os: browserDetails.os.name || '',
      osVersion: browserDetails.os.version || '',
      spokenLanguage,
      mobileApp: mobileApp ? 'true' : 'false',
    });

    return params.toString();
  };
}

export default SignalingSocket;
